import type { Components, Theme } from "@mui/material/styles";

import {
  borderWidth,
  color,
  opacity,
  spacing,
  weave,
} from "@app/theme/weaveTokens.ts";

type ThemeComponents = Components<Omit<Theme, "components">>;

/**
 * DHIG's link states (`textLink.*` component tokens). Weave's link tokens use
 * px for the underline (1px and 2px, the same at a 16px base), but DHIG sizes
 * them in em so they follow the surrounding text, which is kept here. The
 * focus state is an outline in DHIG and a box shadow ring in Weave; the outline
 * stays (R6, DEVX-11164).
 */
const underline = { thin: "0.0625em", thick: "0.125em", offset: "0.125em" };

const linkState = (
  decorationColor: string,
  options: { color?: string; outlineColor?: string; thick?: boolean } = {}
) => ({
  color: options.color ?? color.ink,
  outlineColor: options.outlineColor ?? color.transparent,
  outlineOffset: weave.generic.px["2"],
  outlineStyle: "solid" as const,
  outlineWidth: borderWidth.thick,
  textDecoration: "underline" as const,
  textDecorationColor: decorationColor,
  textDecorationThickness: options.thick ? underline.thick : underline.thin,
  textUnderlineOffset: underline.offset,
});

const textLink = {
  defaultState: linkState(color.ink),
  hoverState: linkState(color.irisHover),
  focusState: linkState(color.ink, { outlineColor: color.iris }),
  pressedState: linkState(color.success, { thick: true }),
  visitedState: linkState(color.inkVisited, { color: color.inkVisited }),
};

/**
 * Restores DHIG's text colour, link and menu item looks on top of Weave.
 * Weave sets Typography to pure black where DHIG uses `text-on-background`
 * (rgb(33, 33, 33)). The font stack fallback (`Arial, sans-serif`) is restored
 * for every Weave component slot by `dhigFontFallbackOverrides`, not here.
 *
 * All slots are plain objects so they merge with Weave's.
 */
export const typographyOverrides: ThemeComponents = {
  MuiTypography: {
    styleOverrides: {
      root: { color: color.inkText },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      // DHIG's `padding: 8px 20px` shorthand comes after its `paddingLeft`, so
      // items sit 20px from the left edge. Weave's own `paddingLeft` is 12px.
      root: {
        paddingLeft: spacing.px20,
        // Weave's Select menu lacks DHIG's option layout: the option text keeps
        // 24px clear of the absolutely placed checked icon.
        "& .DhigSelect--checkedIcon": {
          position: "absolute",
          right: spacing.xs,
        },
        "& .DhigSelect--option": {
          marginRight: spacing.l,
          width: "100%",
        },
      },
    },
  },
  MuiLink: {
    styleOverrides: {
      // DHIG links inherit font from the surrounding text. Weave sets its own
      // family, size, weight and line height on every link.
      root: {
        ...textLink.defaultState,
        fontFamily: undefined,
        fontSize: undefined,
        fontWeight: undefined,
        lineHeight: undefined,
        '&[class*="MuiLink-underlineHover"]': {
          textDecoration: textLink.defaultState.textDecoration,
        },
        '&[class*="MuiTypography-root"]': {
          "&:visited": textLink.visitedState,
          "&:hover, &:visited:hover": textLink.hoverState,
          "&:focus-visible": textLink.focusState,
          "&:active": textLink.pressedState,
          "&[disabled]": {
            opacity: opacity.disabled,
            pointerEvents: "none",
            cursor: "default",
          },
        },
        // Weave's own focus ring is a box shadow, DHIG's is the outline above.
        "&:focus-visible": { boxShadow: "none" },
      },
      underlineNone: { textDecoration: "none" },
    },
  },
};
